/*
 * Mirrors Weglot's language list into the theme's localization drawer.
 *
 * Weglot ships its own floating switcher, which sits outside the theme and
 * duplicates a control the drawer already has a tab for. This reads the
 * language list straight off the Weglot instance — no hardcoded locales — and
 * renders it with the drawer's own markup so it inherits the theme styling,
 * including the checked state. Switching is delegated back to Weglot.
 *
 * The native Shopify language form stays in the markup and is only hidden once
 * this list renders, so the drawer still works if Weglot fails to load.
 */
(() => {
  const READY_TIMEOUT_MS = 10000;
  const POLL_INTERVAL_MS = 50;
  const RADIO_NAME = "weglot_language_code";

  const SELECTORS = {
    weglotList: "[data-weglot-language-list]",
    nativeList: "[data-native-language-list]",
    triggerLabel:
      ".wt-localization-trigger--lang .wt-localization-trigger__label",
    drawer: "localization-drawer",
    closeButton: ".wt-localization-drawer__close",
  };

  const isWeglotReady = () => {
    const weglot = window.Weglot;

    return Boolean(
      weglot &&
        typeof weglot.switchTo === "function" &&
        typeof weglot.getCurrentLang === "function" &&
        weglot.getCurrentLang(),
    );
  };

  /* Weglot's own config is the source of truth for which locales exist. */
  const getLanguageCodes = () => {
    const weglot = window.Weglot;
    const original = weglot.options?.language_from || "";
    let codes = [];

    if (typeof weglot.getAvailableLanguages === "function") {
      codes = weglot.getAvailableLanguages() || [];
    }

    if (!codes.length && Array.isArray(weglot.options?.languages)) {
      codes = weglot.options.languages
        .filter((entry) => entry && entry.enabled !== false)
        .map((entry) => entry.language_to);
    }

    // Some Weglot versions omit the original language from that list.
    const ordered = codes.includes(original) ? codes : [original, ...codes];

    return [...new Set(ordered)].filter(Boolean);
  };

  const getLanguageName = (code) => {
    const weglot = window.Weglot;

    const configured = (weglot.options?.languages || []).find(
      (entry) => entry?.language_to === code && entry.custom_name,
    );
    if (configured) return configured.custom_name;

    if (typeof weglot.getLanguageName === "function") {
      const name = weglot.getLanguageName(code);
      if (name) return name;
    }

    // Fall back to the endonym — the language's name in its own language.
    try {
      const name = new Intl.DisplayNames([code], { type: "language" }).of(code);
      if (name) return name.charAt(0).toUpperCase() + name.slice(1);
    } catch {
      // Intl.DisplayNames is unavailable, or the code is not a known locale.
    }

    return code.toUpperCase();
  };

  const buildItem = (code, currentCode) => {
    const item = document.createElement("li");
    item.className = "wt-language-list__item";

    const input = document.createElement("input");
    input.type = "radio";
    input.name = RADIO_NAME;
    input.id = `wt-weglot-lang_${code}`;
    input.className = "wt-language-list__radio";
    input.value = code;
    input.checked = code === currentCode;
    input.tabIndex = -1;

    const label = document.createElement("label");
    label.className = "wt-language-list__label";
    label.setAttribute("for", input.id);
    label.tabIndex = 0;
    label.textContent = getLanguageName(code);

    item.append(input, label);

    return item;
  };

  const closeDrawer = () => {
    const drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer?.classList.contains("wt-localization-drawer--open")) return;

    drawer.querySelector(SELECTORS.closeButton)?.click();
  };

  const syncTriggerLabels = (code) => {
    document.querySelectorAll(SELECTORS.triggerLabel).forEach((label) => {
      label.textContent = code.toUpperCase();
    });
  };

  const switchLanguage = (code) => {
    if (!code || code === window.Weglot.getCurrentLang()) {
      closeDrawer();
      return;
    }

    syncTriggerLabels(code);
    closeDrawer();
    window.Weglot.switchTo(code);
  };

  const bindInteractions = (list) => {
    list.addEventListener("change", (event) => {
      const input = event.target;
      if (input?.name !== RADIO_NAME) return;

      switchLanguage(input.value);
    });

    // A focused <label> does not respond to Enter on its own.
    list.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;

      const label = event.target.closest?.(".wt-language-list__label");
      if (!label) return;

      event.preventDefault();
      document.getElementById(label.htmlFor)?.click();
    });
  };

  const render = () => {
    const list = document.querySelector(SELECTORS.weglotList);
    if (!list) return false;

    const codes = getLanguageCodes();
    if (codes.length < 2) return false;

    const currentCode = window.Weglot.getCurrentLang();
    const items = codes.map((code) => buildItem(code, currentCode));

    list.replaceChildren(...items);
    list.hidden = false;

    document
      .querySelectorAll(SELECTORS.nativeList)
      .forEach((native) => (native.hidden = true));

    syncTriggerLabels(currentCode);

    return true;
  };

  const onReady = () => {
    if (!render()) return;

    const list = document.querySelector(SELECTORS.weglotList);
    if (list) bindInteractions(list);

    if (typeof window.Weglot.on === "function") {
      window.Weglot.on("languageChanged", (code) => {
        if (code) syncTriggerLabels(code);
      });
    }
  };

  const waitForWeglot = (startedAt) => {
    if (isWeglotReady()) {
      onReady();
      return;
    }

    if (window.performance.now() - startedAt >= READY_TIMEOUT_MS) {
      // Not an error: the store can run without Weglot, in which case the
      // native Shopify language form stays visible and handles the drawer.
      return;
    }

    window.setTimeout(() => waitForWeglot(startedAt), POLL_INTERVAL_MS);
  };

  const start = () => waitForWeglot(window.performance.now());

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
